package com.patronat.top.ui.molecules.inputs

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.joelkanyi.jcomposecountrycodepicker.data.Country
import com.joelkanyi.jcomposecountrycodepicker.resources.Res
import com.joelkanyi.jcomposecountrycodepicker.resources.select_country
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.BodyMedium
import com.patronat.top.ui.theme.MineShaft100
import com.patronat.top.ui.theme.MineShaft500
import com.patronat.top.ui.theme.MineShaft950
import com.patronat.top.ui.theme.White
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun PhoneCountrySelector(
    country: Country?,
    textStyle: TextStyle,
    enabled: Boolean,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .clickable(enabled = enabled, onClick = onClick)
            .padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        if (country != null) {
            Image(
                modifier = Modifier.size(width = 20.dp, height = 14.dp),
                painter = painterResource(country.flag),
                contentDescription = country.name
            )
        }
        Text(
            text = country?.phoneNoCode.orEmpty(),
            style = textStyle.copy(fontWeight = FontWeight.SemiBold)
        )
        Icon(
            painter = AppIcons.ArrowDown,
            contentDescription = "Select country",
            tint = textStyle.color
        )
    }
}

@Composable
internal fun PhoneCountrySelectionDialog(
    countries: List<Country>,
    onDismissRequest: () -> Unit,
    onCountrySelected: (Country) -> Unit
) {
    Dialog(
        onDismissRequest = onDismissRequest,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        BoxWithConstraints {
            val isCompact = maxWidth < 600.dp
            val dialogShape: Shape = if (isCompact) RoundedCornerShape(0.dp) else RoundedCornerShape(16.dp)
            val dialogModifier = if (isCompact) {
                Modifier.fillMaxSize()
            } else {
                Modifier
                    .widthIn(min = 280.dp, max = 560.dp)
                    .fillMaxWidth(0.85f)
                    .fillMaxHeight(0.75f)
            }

            Surface(
                modifier = dialogModifier,
                shape = dialogShape,
                color = White
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(White)
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 8.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        IconButton(onClick = onDismissRequest) {
                            Icon(
                                painter = AppIcons.ArrowLeft,
                                contentDescription = "Back",
                                tint = MineShaft950
                            )
                        }
                        Text(
                            text = stringResource(Res.string.select_country),
                            modifier = Modifier
                                .weight(1f)
                                .padding(end = 48.dp),
                            style = BodyMedium.copy(
                                fontWeight = FontWeight.SemiBold,
                                color = MineShaft950
                            ),
                            textAlign = TextAlign.Center
                        )
                    }

                    HorizontalDivider(color = MineShaft100)

                    LazyColumn(modifier = Modifier.fillMaxSize()) {
                        items(items = countries, key = { it.code }) { country ->
                            PhoneCountryItem(
                                country = country,
                                onClick = { onCountrySelected(country) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PhoneCountryItem(
    country: Country,
    onClick: () -> Unit
) {
    Column {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(
                    interactionSource = remember { androidx.compose.foundation.interaction.MutableInteractionSource() },
                    indication = null,
                    onClick = onClick
                )
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Image(
                modifier = Modifier.size(width = 30.dp, height = 20.dp),
                painter = painterResource(country.flag),
                contentDescription = country.name
            )
            Text(
                text = country.name,
                modifier = Modifier.weight(1f),
                style = BodyMedium.copy(color = MineShaft950)
            )
            Text(
                text = country.phoneNoCode,
                style = BodyMedium.copy(
                    fontWeight = FontWeight.SemiBold,
                    color = MineShaft500
                )
            )
        }
        HorizontalDivider(color = MineShaft100)
    }
}
